import { MARKETPLACE_TOTAL_CREDITS } from '../constants/marketplace';
import { ICartRemovalResult, IMarketplaceCartLine } from '../types/marketplace';

// Operates only on the integer credit prices the server snapshotted. Deriving a credit price
// from a dollar amount on the client is what makes the wallet and the fit check disagree, so
// there is deliberately no creditPrice(price, budget) here.

export const getCartCreditsUsed = (cart: IMarketplaceCartLine[]): number =>
  cart.reduce((total, line) => total + line.creditPrice * line.quantity, 0);

export const getRemainingCredits = (cart: IMarketplaceCartLine[]): number =>
  MARKETPLACE_TOTAL_CREDITS - getCartCreditsUsed(cart);

export const getCatalogBudget = (fitsBudget: boolean, cart: IMarketplaceCartLine[]): number | null =>
  fitsBudget ? Math.min(Math.max(getRemainingCredits(cart), 0), MARKETPLACE_TOTAL_CREDITS) : null;

export const hasSpentCredits = (budget: number | null): boolean =>
  budget !== null && budget < MARKETPLACE_TOTAL_CREDITS;

export const canAffordCredits = (creditPrice: number, cart: IMarketplaceCartLine[]): boolean =>
  creditPrice <= getRemainingCredits(cart);

export const isProductInCart = (productId: string, cart: IMarketplaceCartLine[]): boolean =>
  cart.some((line) => line.product_id === productId);

export const getRemainingCreditsWithoutProduct = (productId: string, cart: IMarketplaceCartLine[]): number =>
  getRemainingCredits(cart.filter((line) => line.product_id !== productId));

export const canAffordReplacement = (productId: string, creditPrice: number, cart: IMarketplaceCartLine[]): boolean =>
  creditPrice <= getRemainingCreditsWithoutProduct(productId, cart);

export const getCartLineForProduct = (
  productId: string,
  cart: IMarketplaceCartLine[],
): IMarketplaceCartLine | undefined => cart.find((line) => line.product_id === productId);

// Preview only: the server recomputes from stored fields on submit and is authoritative.
export const doesCartFit = (cart: IMarketplaceCartLine[]): boolean =>
  getCartCreditsUsed(cart) <= MARKETPLACE_TOTAL_CREDITS;

// Removes a pick and completes a pending swap in one step. The incoming line takes the removed
// row's position so the list does not reshuffle; if it still does not fit the swap stays pending.
export const resolveCartAfterRemoval = (
  cart: IMarketplaceCartLine[],
  variantId: string,
  swapPending: IMarketplaceCartLine | null,
): ICartRemovalResult => {
  const removedIndex = cart.findIndex((line) => line.variant_id === variantId);
  const removed = removedIndex < 0 ? null : cart[removedIndex];
  const nextCart = cart.filter((line) => line.variant_id !== variantId);

  if (!swapPending || !canAffordCredits(swapPending.creditPrice, nextCart)) {
    return { cart: nextCart, swapped: null, removed };
  }

  const insertAt = removedIndex < 0 ? nextCart.length : removedIndex;
  const withSwap = [...nextCart.slice(0, insertAt), swapPending, ...nextCart.slice(insertAt)];

  return { cart: withSwap, swapped: swapPending, removed };
};
